import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";

export const Route = createFileRoute("/payment-safety")({
  head: () => ({
    meta: [
      { title: "Payment Safety — Want To Buy" },
      {
        name: "description",
        content:
          "Want To Buy does not process payments or mediate disputes. This page explains why PayPal Goods & Services is strongly recommended, and how to use your Want To Buy message thread as evidence in a PayPal dispute.",
      },
    ],
  }),
  component: PaymentSafetyPage,
});

function PaymentSafetyPage() {
  return (
    <DocPage
      eyebrow="Buyer &amp; Seller Protection"
      title="Payment Safety"
      intro="Want To Buy is a connection platform. It does not process payments or mediate disputes. This page explains how to arrange payment safely and how to handle a transaction that goes wrong."
    >
      <p className="!mt-0 text-xs uppercase tracking-wider text-muted-foreground">Last updated: July 2026</p>

      <section className="panel-note">
        <p className="!mt-0">
          <strong>Want To Buy does not process payments, hold funds, or mediate disputes.</strong> Once buyer and seller are connected through the message thread, payment is arranged entirely between the two parties. Want To Buy strongly recommends <strong>PayPal Goods &amp; Services</strong> because PayPal — not Want To Buy — provides independent buyer and seller protection through their own terms.
        </p>
      </section>

      <section>
        <h2>Why PayPal Goods &amp; Services</h2>
        <p>
          PayPal Goods &amp; Services is the only widely-available payment method that comes with independent, third-party buyer and seller protection. Because Want To Buy provides no protection of its own, using PayPal G&amp;S is the single most important thing you can do to protect yourself in any transaction arranged through the platform.
        </p>
      </section>

      <section>
        <h2>PayPal Buyer Protection — what's covered</h2>
        <p>When you pay with PayPal Goods &amp; Services, PayPal's Buyer Protection generally covers:</p>
        <ul>
          <li><strong>Items not received</strong> — the seller charged you but never shipped, or tracking never shows delivery.</li>
          <li><strong>Items significantly not as described</strong> — the card you received is materially different from what the seller documented (wrong card, wrong condition, counterfeit, damage not disclosed).</li>
          <li><strong>Unauthorized transactions</strong> — a payment made from your PayPal account without your permission.</li>
        </ul>
      </section>

      <section>
        <h2>PayPal Buyer Protection — what's not covered</h2>
        <ul>
          <li>Payments via PayPal Friends &amp; Family, bank transfer, cryptocurrency, or any method other than PayPal Goods &amp; Services.</li>
          <li>Buyer's remorse or a change of mind.</li>
          <li>Condition disputes where the item matches the seller's timestamped photos and agreed description.</li>
        </ul>
        <p className="panel-note">
          <strong>Warning.</strong> If a seller asks you to pay via Friends &amp; Family, bank transfer, cryptocurrency, or any method outside PayPal Goods &amp; Services, refuse. You will have no PayPal protection, and Want To Buy provides no protection of its own.
        </p>
      </section>

      <section>
        <h2>PayPal Seller Protection</h2>
        <p>PayPal Seller Protection generally covers sellers against Item Not Received claims (provided you shipped to the PayPal-confirmed address with tracked delivery) and unauthorized transactions. <strong>Tracked shipping is strongly recommended for every order.</strong> Untracked shipments are not eligible for PayPal Seller Protection.</p>
      </section>

      <section>
        <h2>How to handle a transaction problem</h2>
        <p>
          <strong>Want To Buy does not process payments or mediate disputes.</strong> If a transaction goes wrong, you must resolve it directly through your payment provider.
        </p>
        <ol>
          <li>If you paid via PayPal Goods &amp; Services, open a dispute in the <strong>PayPal Resolution Centre</strong>.</li>
          <li>Pick "Item Not Received" or "Item Significantly Not As Described" as appropriate.</li>
          <li>Attach the evidence described below.</li>
        </ol>
      </section>

      <section>
        <h2>How to use your Want To Buy message thread as evidence</h2>
        <p>
          Want To Buy message threads serve as a <strong>timestamped record</strong> of everything agreed between buyer and seller — card details, condition, price, shipping terms, and the mandatory timestamped photos the seller submitted. Screenshots of the thread are accepted as evidence in PayPal Significantly Not as Described and Item Not Received disputes.
        </p>
        <p>Before opening a PayPal dispute:</p>
        <ol>
          <li>Screenshot the <strong>buy order page</strong> showing the agreed card, condition, and price.</li>
          <li>Screenshot the <strong>2 mandatory timestamped photos</strong> submitted by the seller (front and back with handwritten note).</li>
          <li>Screenshot the <strong>full message thread</strong> showing the agreed price, condition, and any shipping communications.</li>
          <li>If applicable, screenshot any additional photos the seller submitted at your request.</li>
          <li>Submit these screenshots as evidence when opening a PayPal Goods &amp; Services dispute.</li>
        </ol>
        <p>
          Want To Buy will not provide transaction records directly to third parties except when legally required by court order or law enforcement request. The screenshots you take yourself from your own account are the primary evidence channel.
        </p>
      </section>

      <section>
        <h2>Official PayPal references</h2>
        <ul>
          <li>
            <a href="https://www.paypal.com/us/legalhub/buyer-protection" target="_blank" rel="noopener noreferrer">
              PayPal Buyer Protection — official page
            </a>
          </li>
          <li>
            <a href="https://www.paypal.com/us/legalhub/seller-protection" target="_blank" rel="noopener noreferrer">
              PayPal Seller Protection — official page
            </a>
          </li>
          <li>
            <a href="https://www.paypal.com/us/smarthelp/topic/DISPUTES" target="_blank" rel="noopener noreferrer">
              PayPal Resolution Centre &amp; disputes help
            </a>
          </li>
        </ul>
        <p className="text-xs text-muted-foreground">
          Please read PayPal's own terms for the complete and binding description of what Buyer and Seller Protection cover. This page is a plain-language summary provided by Want To Buy to help our community understand how payment protection works when using PayPal.
        </p>
      </section>
    </DocPage>
  );
}
